import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  successMessage?: string;
  errorMessage?: string;
  onConfirm: () => Promise<void> | void;
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  successMessage,
  errorMessage,
  onConfirm,
  isLoading = false,
}) => {
  const handleConfirm = async () => {
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px] p-0 gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5">
          <DialogTitle className="text-base font-semibold text-slate-900">
            {title || `Confirm`}
          </DialogTitle>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors flex-shrink-0"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <DialogDescription className="text-sm text-slate-600">
            {description || `Are you sure you want to confirm this action?`}
          </DialogDescription>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 pt-3 pb-5">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-lg"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={isLoading}
            className="rounded-lg bg-brand-teal hover:bg-brand-teal/90 text-white"
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
